import React, { useEffect, useMemo, useState } from 'react';
import PromotionBriefingEditor from './PromotionBriefingEditor';
import PromotionCardEditor from './PromotionCardEditor';
import PromotionTestPanel from './PromotionTestPanel';
import PromotionEditorSection, {
  type PromotionSectionState,
} from './PromotionEditorSection';
import { PROMOTION_SECTION_HELP } from './promotionEditorHelp';
import {
  formatPromotionShortDate,
  hasCardVariable,
  isInviteButtonLink,
  isInvitePromotion,
  parseUkDateInput,
  promotionAudienceSummary,
  promotionChannelLabel,
  promotionSkipReasonLabel,
  PROMOTION_NO_SOURCE,
  type PromotionDateInputs,
  type PromotionDefinition,
  type PromotionDetail,
  type PromotionOptions,
  type PromotionPreview,
  type PromotionSummary,
} from './promotionTypes';

interface ValidationIssue {
  section: number;
  message: string;
  saveBlocking: boolean;
}

interface PromotionEditorProps {
  definition: PromotionDefinition;
  dateInputs: PromotionDateInputs;
  options: PromotionOptions | null;
  detail: PromotionDetail | null;
  current: PromotionSummary | null;
  selectedSlug: string | null;
  preview: PromotionPreview | null;
  busy: string | null;
  hasUnsavedChanges: boolean;
  onChange: (definition: PromotionDefinition) => void;
  onDateInputChange: (field: keyof PromotionDateInputs, value: string) => void;
  onSave: () => void;
  onLaunch: () => void;
  onCheckAudience: () => void;
  onBack: () => void;
  auth: () => Promise<{ headers: { Authorization: string } }>;
  onCardSubmitted: () => Promise<void>;
}

const VAGUE_BUTTONS = new Set([
  'ok',
  'yes',
  'click here',
  'learn more',
  'more',
]);
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
type PromotionSectionNumber = 1 | 2 | 3 | 4 | 5;
const PROMOTION_SECTION_TITLES: Record<PromotionSectionNumber, string> = {
  1: 'Feature',
  2: 'Audience',
  3: 'Channels',
  4: 'Schedule and limits',
  5: 'Review',
};

const PromotionSectionHelp: React.FC<{
  number: PromotionSectionNumber;
  title: string;
}> = ({ number, title }) => {
  const help = PROMOTION_SECTION_HELP[number];
  return (
    <div className='promotion-editor-help'>
      <h3 className='promotion-editor-help__heading'>
        {number}. {title}
      </h3>
      <p className='promotion-editor-help__intro'>{help.intro}</p>
      <ul>
        {help.points.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </div>
  );
};

const cardCopyWarnings = (
  language: string,
  copy: { body: string; button: string },
  hasMedia: boolean,
) => {
  const warnings: string[] = [];
  const estimatedLines = copy.body
    .split('\n')
    .reduce(
      (total, paragraph) =>
        total + Math.max(1, Math.ceil(paragraph.length / 38)),
      0,
    );
  if (hasMedia && estimatedLines > 3) {
    warnings.push(
      `${language}: WhatsApp will fold this after about 3 lines; the rest is behind 'Read more'.`,
    );
  }
  if (VAGUE_BUTTONS.has(copy.button.trim().toLowerCase())) {
    warnings.push(`${language}: Make the button label describe the tap.`);
  }
  if (/https?:\/\//i.test(copy.body)) {
    warnings.push(`${language}: Links in card copy aren't tracked.`);
  }
  return warnings;
};

const cardReviewWarnings = (definition: PromotionDefinition) => {
  if (!definition.card) return [];
  const warnings = cardCopyWarnings(
    'EN',
    definition.card,
    Boolean(definition.card.media),
  );
  Object.entries(definition.card.translations).forEach(([language, copy]) => {
    if (copy) {
      warnings.push(
        ...cardCopyWarnings(
          language.toUpperCase(),
          copy,
          Boolean(copy.media ?? definition.card?.media),
        ),
      );
    }
  });
  return warnings;
};

const validateCard = (definition: PromotionDefinition): string[] => {
  if (!definition.card) return [];
  const card = definition.card;
  const errors: string[] = [];
  const validCopy = (copy: {
    body: string;
    footer: string | null;
    button: string;
  }) =>
    Boolean(
      copy.body.trim() &&
        copy.body.length <= 1024 &&
        (copy.footer === null || copy.footer.length <= 60) &&
        copy.button.trim() &&
        copy.button.length <= 20 &&
        !hasCardVariable(copy.body) &&
        !hasCardVariable(copy.footer) &&
        !hasCardVariable(copy.button),
    );
  if (!validCopy(card)) {
    errors.push('Complete the WhatsApp card copy within its character limits.');
  }
  if (
    Object.values(card.translations).some((copy) => copy && !validCopy(copy))
  ) {
    errors.push('Complete each WhatsApp card translation within its limits.');
  }
  return errors;
};

const validationIssues = (
  definition: PromotionDefinition,
  dateInputs: PromotionDateInputs,
): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const issue = (section: number, message: string, saveBlocking = false) =>
    issues.push({ section, message, saveBlocking });

  if (!definition.name.trim()) {
    issue(1, 'Promotion name is required.', true);
  }
  if (definition.name.length > 80) {
    issue(1, 'Promotion name must be 80 characters or fewer.', true);
  }
  if (!definition.whatsapp.assistantNote.trim()) {
    issue(1, 'What the secretary should know is required.');
  }
  if (definition.whatsapp.assistantNote.length > 1500) {
    issue(1, 'Secretary note must be 1500 characters or fewer.', true);
  }
  if (definition.link?.kind === 'url') {
    try {
      if (new URL(definition.link.url).protocol !== 'https:') {
        issue(1, 'Link must use HTTPS.', true);
      }
    } catch {
      issue(1, 'Enter a valid HTTPS link.', true);
    }
  }
  if (definition.outcome === 'link_clicked' && definition.link === null) {
    issue(1, 'Link-click outcome requires a link.');
  }
  if (definition.audience.userIds.length > 5000) {
    issue(2, 'Use no more than 5000 user IDs.', true);
  }
  if (
    !definition.audience.allUsers &&
    definition.audience.sources.length === 0 &&
    definition.audience.userIds.length === 0
  ) {
    issue(2, 'Choose who gets this: All users, a source or user IDs.');
  }
  if (
    !Number.isInteger(definition.holdoutPercent) ||
    definition.holdoutPercent < 0 ||
    definition.holdoutPercent > 50
  ) {
    issue(2, 'Holdout must be between 0 and 50%.', true);
  }
  if (definition.channels.length === 0) {
    issue(3, 'Select at least one channel.');
  }
  const usesWhatsApp = definition.channels.includes('whatsapp');
  if (usesWhatsApp) {
    const cardOnly = definition.whatsapp.cardOnly === true;
    if (!cardOnly && definition.whatsapp.guidance.length < 20) {
      issue(
        3,
        'How should the secretary write this message must be at least 20 characters.',
      );
    }
    if (definition.whatsapp.guidance.length > 1500) {
      issue(
        3,
        'How should the secretary write this message must be 1500 characters or fewer.',
        true,
      );
    }
    if (!cardOnly && !definition.whatsapp.fallbackText.length) {
      issue(3, 'WhatsApp fallback message is required.');
    }
    if (definition.whatsapp.fallbackText.length > 900) {
      issue(
        3,
        'WhatsApp fallback message must be 900 characters or fewer.',
        true,
      );
    }
    const hasDoubleBraceLink =
      definition.whatsapp.fallbackText.includes('{{link}}');
    const hasValidLinkPlaceholder =
      definition.whatsapp.fallbackText.includes('{link}') &&
      !hasDoubleBraceLink;
    if (cardOnly) {
      if (!definition.card) issue(3, 'Card only needs a WhatsApp card.');
    } else if (hasDoubleBraceLink) {
      issue(3, 'Use {link}, not {{link}}.');
    } else if (isInviteButtonLink(definition.link) && hasValidLinkPlaceholder) {
      issue(
        3,
        'Fallback message must not contain {link} when the invite link is a button.',
      );
    } else if (
      (definition.link !== null && !isInviteButtonLink(definition.link)) !==
      hasValidLinkPlaceholder
    ) {
      issue(
        3,
        'WhatsApp fallback must contain {link} if and only if a link is set.',
      );
    }
    const cardCopyTooLong = (copy: {
      body: string;
      footer: string | null;
      button: string;
    }) =>
      copy.body.length > 1024 ||
      (copy.footer !== null && copy.footer.length > 60) ||
      copy.button.length > 20;
    const hasCardCopyTooLong =
      definition.card !== null &&
      (cardCopyTooLong(definition.card) ||
        Object.values(definition.card.translations).some(
          (copy) => copy !== undefined && cardCopyTooLong(copy),
        ));
    validateCard(definition).forEach((message) =>
      issue(3, message, hasCardCopyTooLong),
    );
  } else if (definition.card) {
    issue(3, 'WhatsApp card requires the WhatsApp channel.');
  }
  if (definition.channels.includes('briefing')) {
    if (!definition.briefing) {
      issue(3, 'Daily Briefing content is required.');
    } else {
      if (!definition.briefing.headline.trim()) {
        issue(3, 'Daily Briefing headline is required.');
      } else if (definition.briefing.headline.length > 60) {
        issue(
          3,
          'Daily Briefing headline must be 60 characters or fewer.',
          true,
        );
      }
      if (!definition.briefing.body.trim()) {
        issue(3, 'Daily Briefing copy is required.');
      } else if (definition.briefing.body.length > 200) {
        issue(3, 'Daily Briefing copy must be 200 characters or fewer.', true);
      }
      if (
        definition.briefing.imageUrl &&
        !definition.briefing.imageUrl.startsWith('https://')
      ) {
        issue(3, 'Daily Briefing image URL must use HTTPS.', true);
      }
    }
  }
  const startDate = parseUkDateInput(dateInputs.startDate);
  const endDate = parseUkDateInput(dateInputs.endDate);
  if (!startDate) {
    issue(4, 'Enter a valid start date (dd/mm/yyyy).', true);
  }
  if (!endDate) {
    issue(4, 'Enter a valid end date (dd/mm/yyyy).', true);
  } else if (startDate && endDate < startDate) {
    issue(4, 'End date must be on or after the start date.', true);
  }
  if (
    definition.afterOnboardingDays !== null &&
    (!Number.isInteger(definition.afterOnboardingDays) ||
      definition.afterOnboardingDays < 1 ||
      definition.afterOnboardingDays > 90)
  ) {
    issue(
      4,
      'Days after onboarding must be a whole number from 1 to 90.',
      true,
    );
  }
  if (definition.slot.weekdays.length === 0) {
    issue(4, 'Select at least one weekday.');
  }
  if (definition.slot.partsOfDay.length === 0) {
    issue(4, 'Select at least one part of day.');
  }
  return issues;
};

export const hasSaveBlockingPromotionIssues = (
  definition: PromotionDefinition,
  dateInputs: PromotionDateInputs,
) =>
  validationIssues(definition, dateInputs).some((entry) => entry.saveBlocking);

const scheduleSummary = (definition: PromotionDefinition) => {
  const weekdays = [...definition.slot.weekdays].sort((a, b) => a - b);
  const days =
    weekdays.length === 5 &&
    [1, 2, 3, 4, 5].every((day) => weekdays.includes(day))
      ? 'Mon–Fri'
      : weekdays.map((day) => WEEKDAYS[day]).join(', ') || 'No days';
  const partOfDayLabels = {
    morning: 'mornings',
    afternoon: 'afternoons',
    evening: 'evenings',
  };
  const partOfDay = definition.slot.partsOfDay
    .map((part) => partOfDayLabels[part])
    .join(' or ');
  const afterOnboarding =
    definition.afterOnboardingDays === null
      ? ''
      : `${definition.afterOnboardingDays} days after onboarding · `;
  return `${afterOnboarding}${formatPromotionShortDate(definition.startDate)} – ${formatPromotionShortDate(definition.endDate)} · ${days} ${partOfDay}`;
};

const PromotionEditor: React.FC<PromotionEditorProps> = ({
  definition,
  dateInputs,
  options,
  detail,
  current,
  selectedSlug,
  preview,
  busy,
  hasUnsavedChanges,
  onChange,
  onDateInputChange,
  onSave,
  onLaunch,
  onCheckAudience,
  onBack,
  auth,
  onCardSubmitted,
}) => {
  const [openSections, setOpenSections] = useState<Set<number>>(
    () => new Set(selectedSlug ? [] : [1]),
  );
  const [activeSection, setActiveSection] = useState<PromotionSectionNumber>(1);
  useEffect(() => {
    if (openSections.size === 0 || openSections.has(activeSection)) return;
    const lowestOpenSection = Array.from(openSections).sort((a, b) => a - b)[0];
    if (lowestOpenSection !== undefined) {
      setActiveSection(lowestOpenSection as PromotionSectionNumber);
    }
  }, [activeSection, openSections]);
  const issues = useMemo(
    () => validationIssues(definition, dateInputs),
    [definition, dateInputs],
  );
  const saveIssues = issues.filter((entry) => entry.saveBlocking);
  const visibleIssues =
    saveIssues.length > 0
      ? saveIssues
      : issues.filter((entry) => !entry.saveBlocking);
  const cardLocked = Boolean(
    detail?.cardTemplates?.some(
      (template) => template.status !== 'not_submitted',
    ),
  );
  const englishCardApproved = detail?.cardTemplates?.some(
    (template) => template.language === 'en' && template.status === 'approved',
  );
  const launchBlocked =
    hasUnsavedChanges ||
    issues.length > 0 ||
    Boolean(definition.card && !englishCardApproved);
  const configuredSources = options?.sources ?? [];
  const sourceOptions = [
    configuredSources.find(({ key }) => key === PROMOTION_NO_SOURCE) ?? {
      key: PROMOTION_NO_SOURCE,
      label: 'No recorded source',
      group: '',
    },
    ...configuredSources
      .filter(({ key }) => key !== PROMOTION_NO_SOURCE)
      .sort((left, right) => left.label.localeCompare(right.label)),
  ];
  const featureFlags = [
    ...(options?.flags ?? []),
    ...definition.audience.requiredFlags.filter(
      (flag) => !(options?.flags ?? []).includes(flag),
    ),
  ];
  const targetedSourceKeys = new Set(definition.audience.sources);
  const availableSources = sourceOptions.filter(
    (source) => !targetedSourceKeys.has(source.key),
  );
  const targetedSources = Array.from(targetedSourceKeys)
    .map(
      (key) =>
        sourceOptions.find((source) => source.key === key) ?? {
          key,
          label: key,
          group: '',
        },
    )
    .sort((left, right) => left.label.localeCompare(right.label));

  const update = <K extends keyof PromotionDefinition>(
    key: K,
    value: PromotionDefinition[K],
  ) => onChange({ ...definition, [key]: value });
  const updateAudienceSources = (sources: string[]) =>
    update('audience', { ...definition.audience, sources });
  const updateWhatsApp = (patch: Partial<PromotionDefinition['whatsapp']>) =>
    update('whatsapp', { ...definition.whatsapp, ...patch });
  const toggleSection = (number: PromotionSectionNumber) => {
    if (!openSections.has(number)) setActiveSection(number);
    setOpenSections((currentSections) => {
      const next = new Set(currentSections);
      if (next.has(number)) next.delete(number);
      else next.add(number);
      return next;
    });
  };
  const continueSection = (number: PromotionSectionNumber) => {
    const nextSection =
      number < 5 ? ((number + 1) as PromotionSectionNumber) : number;
    setActiveSection(nextSection);
    setOpenSections((currentSections) => {
      const next = new Set(currentSections);
      next.delete(number);
      if (number < 5) next.add(number + 1);
      return next;
    });
  };
  const mobileSectionHelp = (number: PromotionSectionNumber) => (
    <details className='promotion-editor-section__help'>
      <summary>How this works</summary>
      <PromotionSectionHelp
        number={number}
        title={PROMOTION_SECTION_TITLES[number]}
      />
    </details>
  );
  const setChannel = (channel: 'whatsapp' | 'briefing', checked: boolean) => {
    const channels = checked
      ? [...definition.channels, channel]
      : definition.channels.filter(
          (currentChannel) => currentChannel !== channel,
        );
    onChange({
      ...definition,
      channels: (['whatsapp', 'briefing'] as const).filter((value) =>
        channels.includes(value),
      ),
      ...(channel === 'briefing'
        ? {
            briefing: checked
              ? (definition.briefing ?? {
                  headline: '',
                  body: '',
                  imageUrl: null,
                })
              : null,
          }
        : { card: checked ? definition.card : null }),
    });
  };

  const sectionState = (number: number): PromotionSectionState => {
    if (issues.some((entry) => entry.section === number)) return 'problem';
    if (number === 1) {
      return definition.name.trim() && definition.whatsapp.assistantNote.trim()
        ? 'complete'
        : 'incomplete';
    }
    if (number === 2) return 'complete';
    if (number === 3) {
      return definition.channels.length > 0 ? 'complete' : 'incomplete';
    }
    if (number === 4) {
      return parseUkDateInput(dateInputs.startDate) &&
        parseUkDateInput(dateInputs.endDate) &&
        definition.slot.weekdays.length > 0
        ? 'complete'
        : 'incomplete';
    }
    return issues.length === 0 ? 'complete' : 'problem';
  };
  const sectionSummary = (number: number) => {
    if (number === 1) {
      return `${definition.name || 'Add a name'} · ${definition.link ? 'link set' : 'no link'}`;
    }
    if (number === 2) {
      const sourceLabels = Object.fromEntries(
        (options?.sources ?? []).map(({ key, label }) => [key, label]),
      );
      const audienceSummary = promotionAudienceSummary(
        definition.audience,
        definition.holdoutPercent,
        sourceLabels,
      );
      return `${audienceSummary}${
        preview ? ` · ${preview.eligible} eligible` : ''
      }`;
    }
    if (number === 3) {
      return (
        definition.channels.map(promotionChannelLabel).join(' · ') ||
        'Choose at least one channel'
      );
    }
    if (number === 4) return scheduleSummary(definition);
    return issues.length
      ? `${issues.length} ${issues.length === 1 ? 'thing' : 'things'} to fix`
      : 'Ready to launch';
  };

  const allCardWarnings = Array.from(
    new Set([
      ...cardReviewWarnings(definition),
      ...Object.entries(detail?.cardWarnings ?? {}).flatMap(
        ([language, warnings]) =>
          (warnings ?? []).map(
            (warning) => `${language.toUpperCase()}: ${warning.message}`,
          ),
      ),
    ]),
  );
  if (definition.card && !englishCardApproved) {
    allCardWarnings.unshift(
      'English WhatsApp card needs approval before launch.',
    );
  }

  const previewContent = preview ? (
    <div className='promotion-audience-preview'>
      <p>
        <strong>{preview.eligible}</strong> eligible ·{' '}
        {Object.values(preview.skipped).reduce((sum, count) => sum + count, 0)}{' '}
        skipped
      </p>
      {preview.cardCost && (
        <p className='promotion-card-cost'>
          Card cost estimate: £{preview.cardCost.estimatedGbp.toFixed(2)} for{' '}
          {Math.round(
            (preview.cardCost.outsideWindow *
              (100 - definition.holdoutPercent)) /
              100,
          )}{' '}
          people outside the 24-hour window (Meta&apos;s rates from 1 Oct 2026).{' '}
          {preview.cardCost.insideWindow} people inside the window get the
          secretary&apos;s message instead (free).
        </p>
      )}
      {Object.entries(preview.skipped).some(([, count]) => count > 0) && (
        <ul>
          {Object.entries(preview.skipped)
            .filter(([, count]) => count > 0)
            .map(([reason, count]) => (
              <li key={reason}>
                {promotionSkipReasonLabel(reason)}: {count}
              </li>
            ))}
        </ul>
      )}
      <p>
        Sample (up to 50):{' '}
        {preview.sample.map((row) => row.name || row.userId).join(', ') ||
          'None'}
      </p>
    </div>
  ) : (
    <p className='launch-hint'>Check the audience to see eligible people.</p>
  );

  return (
    <div className='promotion-editor'>
      <header className='promotion-editor__topbar'>
        <button type='button' className='promotion-back-link' onClick={onBack}>
          ← Promotions
        </button>
        <div className='promotion-editor__title'>
          <strong>{definition.name || 'New promotion'}</strong>
          <span className='launch-status'>Draft</span>
        </div>
        <div className='promotion-editor__actions'>
          <button
            type='button'
            className='launch-btn launch-btn--quiet'
            disabled={busy !== null || saveIssues.length > 0}
            onClick={onSave}
          >
            {busy === 'save' ? 'Saving…' : 'Save'}
          </button>
          <button
            type='button'
            className='launch-btn launch-btn--primary'
            disabled={busy !== null || launchBlocked}
            title={
              hasUnsavedChanges
                ? 'Save your changes first'
                : definition.card && !englishCardApproved
                  ? 'English card needs WhatsApp approval before launch'
                  : undefined
            }
            onClick={onLaunch}
          >
            {busy === 'launch' ? 'Launching…' : 'Launch'}
          </button>
        </div>
      </header>
      {issues.length > 0 && (
        <div className='promotion-validation-note' role='alert'>
          <strong>
            {saveIssues.length > 0
              ? 'Fix these before saving:'
              : 'Finish these before launch:'}
          </strong>
          <ul>
            {visibleIssues.map((entry, index) => (
              <li key={`${entry.section}-${entry.message}-${index}`}>
                {entry.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className='promotion-editor__layout'>
        <div className='promotion-editor__sections'>
          <PromotionEditorSection
            number={1}
            title={PROMOTION_SECTION_TITLES[1]}
            summary={sectionSummary(1)}
            state={sectionState(1)}
            open={openSections.has(1)}
            onToggle={() => toggleSection(1)}
            onContinue={() => continueSection(1)}
            help={mobileSectionHelp(1)}
          >
            <div className='promotion-form'>
              <label>
                Name
                <input
                  value={definition.name}
                  maxLength={80}
                  onChange={(event) => update('name', event.target.value)}
                />
              </label>
              <label>
                Link
                <select
                  value={
                    definition.link === null
                      ? 'none'
                      : definition.link.kind === 'invite'
                        ? definition.link.button
                          ? 'invite_button'
                          : 'invite'
                        : 'url'
                  }
                  onChange={(event) =>
                    update(
                      'link',
                      event.target.value === 'invite_button'
                        ? { kind: 'invite', button: true }
                        : event.target.value === 'invite'
                          ? { kind: 'invite' }
                          : event.target.value === 'url'
                            ? {
                                kind: 'url',
                                url:
                                  definition.link?.kind === 'url'
                                    ? definition.link.url
                                    : '',
                              }
                            : null,
                    )
                  }
                >
                  <option value='none'>None</option>
                  <option value='url'>Fixed URL</option>
                  <option value='invite'>Their invite link</option>
                  <option value='invite_button'>
                    Their invite link (button)
                  </option>
                </select>
              </label>
              {definition.link?.kind === 'url' && (
                <label>
                  HTTPS URL
                  <input
                    value={definition.link.url}
                    placeholder='https://'
                    onChange={(event) =>
                      update('link', {
                        kind: 'url',
                        url: event.target.value,
                      })
                    }
                  />
                </label>
              )}
              <label>
                <span id='promotion-assistant-note-label'>
                  What the secretary should know
                </span>
                <p
                  id='promotion-assistant-note-help'
                  className='promotion-field-lead'
                >
                  This helps your secretary answer questions about this
                  promotion if the user asks, and helps the secretary draft the
                  message
                </p>
                <textarea
                  aria-labelledby='promotion-assistant-note-label'
                  aria-describedby='promotion-assistant-note-help'
                  rows={3}
                  maxLength={1500}
                  value={definition.whatsapp.assistantNote}
                  onChange={(event) =>
                    updateWhatsApp({ assistantNote: event.target.value })
                  }
                />
              </label>
              <label>
                Follow-through
                <select
                  value={
                    definition.followThrough.kind === 'info'
                      ? 'info'
                      : definition.followThrough.kind === 'invites'
                        ? 'invites'
                        : definition.followThrough.useCase
                  }
                  onChange={(event) =>
                    update(
                      'followThrough',
                      event.target.value === 'info'
                        ? { kind: 'info' }
                        : event.target.value === 'invites'
                          ? { kind: 'invites' }
                          : {
                              kind: 'use_case',
                              useCase: event.target.value,
                            },
                    )
                  }
                >
                  <option value='info'>
                    Info only (explain and give the link)
                  </option>
                  <option value='invites'>Sending invites</option>
                  {(options?.useCases ?? []).map((useCase) => (
                    <option key={useCase.slug} value={useCase.slug}>
                      {useCase.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Adoption outcome
                <select
                  value={definition.outcome}
                  onChange={(event) =>
                    update(
                      'outcome',
                      event.target.value as PromotionDefinition['outcome'],
                    )
                  }
                >
                  <option value='positive_reply'>Positive reply</option>
                  <option value='link_clicked' disabled={!definition.link}>
                    Link clicked
                  </option>
                </select>
              </label>
            </div>
          </PromotionEditorSection>

          <PromotionEditorSection
            number={2}
            title={PROMOTION_SECTION_TITLES[2]}
            summary={sectionSummary(2)}
            state={sectionState(2)}
            open={openSections.has(2)}
            onToggle={() => toggleSection(2)}
            onContinue={() => continueSection(2)}
            help={mobileSectionHelp(2)}
          >
            <div className='promotion-form'>
              <strong className='promotion-form__heading'>Who gets this</strong>
              <label className='promotion-check promotion-form__full-row'>
                <input
                  type='radio'
                  name='promotion-audience-target'
                  checked={definition.audience.allUsers}
                  onChange={() =>
                    update('audience', {
                      ...definition.audience,
                      allUsers: true,
                    })
                  }
                />
                All users
              </label>
              <label className='promotion-check promotion-form__full-row'>
                <input
                  type='radio'
                  name='promotion-audience-target'
                  checked={!definition.audience.allUsers}
                  onChange={() =>
                    update('audience', {
                      ...definition.audience,
                      allUsers: false,
                    })
                  }
                />
                Customise users
              </label>
              <fieldset
                className='promotion-customise-users'
                disabled={definition.audience.allUsers}
                aria-label='Customise users'
              >
                <div className='promotion-source-targeting'>
                  <div className='promotion-source-targeting__available'>
                    <p className='promotion-source-targeting__heading'>
                      Available sources
                    </p>
                    <div
                      className='promotion-source-targeting__list'
                      role='group'
                      aria-label='Available sources'
                    >
                      {availableSources.length > 0 ? (
                        availableSources.map((source) => {
                          const moveSource = () =>
                            updateAudienceSources([
                              ...definition.audience.sources,
                              source.key,
                            ]);
                          return (
                            <button
                              type='button'
                              className='promotion-source-targeting__item'
                              key={source.key}
                              onDoubleClick={moveSource}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  moveSource();
                                }
                              }}
                            >
                              {source.group
                                ? `${source.label} · ${source.group}`
                                : source.label}
                            </button>
                          );
                        })
                      ) : (
                        <p className='promotion-source-targeting__empty'>
                          No available sources.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className='promotion-source-targeting__actions'>
                    <button
                      type='button'
                      className='launch-btn launch-btn--quiet'
                      onClick={() =>
                        updateAudienceSources(
                          sourceOptions.map((source) => source.key),
                        )
                      }
                    >
                      All sources
                    </button>
                    <button
                      type='button'
                      className='launch-btn launch-btn--quiet'
                      onClick={() => updateAudienceSources([])}
                    >
                      Clear
                    </button>
                  </div>
                  <div className='promotion-source-targeting__targeted'>
                    <p className='promotion-source-targeting__heading'>
                      Selected sources
                    </p>
                    <div
                      className='promotion-source-targeting__list'
                      role='group'
                      aria-label='Selected sources'
                    >
                      {targetedSources.length > 0 ? (
                        targetedSources.map((source) => {
                          const moveSource = () =>
                            updateAudienceSources(
                              definition.audience.sources.filter(
                                (key) => key !== source.key,
                              ),
                            );
                          return (
                            <button
                              type='button'
                              className='promotion-source-targeting__item'
                              key={source.key}
                              onDoubleClick={moveSource}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  moveSource();
                                }
                              }}
                            >
                              {source.group
                                ? `${source.label} · ${source.group}`
                                : source.label}
                            </button>
                          );
                        })
                      ) : (
                        <p className='promotion-source-targeting__empty'>
                          No sources selected.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <label>
                  Add users by ID
                  <p className='promotion-field-lead'>
                    One per line. These people are added on top of any sources.
                  </p>
                  <textarea
                    rows={3}
                    value={definition.audience.userIds.join('\n')}
                    onChange={(event) =>
                      update('audience', {
                        ...definition.audience,
                        userIds: event.target.value
                          .split(/\r?\n/)
                          .map((id) => id.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </label>
              </fieldset>
              <strong className='promotion-form__heading'>Filters</strong>
              <label className='promotion-check promotion-form__full-row'>
                <input
                  type='checkbox'
                  checked={definition.audience.newUsersOnly}
                  disabled={definition.afterOnboardingDays === null}
                  onChange={(event) =>
                    update('audience', {
                      ...definition.audience,
                      newUsersOnly: event.target.checked,
                    })
                  }
                />
                Only new users
              </label>
              <p className='promotion-field-lead promotion-form__full-row'>
                {definition.afterOnboardingDays === null
                  ? 'Needs "Days after onboarding" in Schedule and limits.'
                  : 'Only people who finish onboarding for the first time after you press Launch. The eligible count still includes existing users.'}
              </p>
              <label className='promotion-check promotion-form__full-row'>
                <input
                  type='checkbox'
                  checked={definition.audience.workAccount}
                  onChange={(event) =>
                    update('audience', {
                      ...definition.audience,
                      workAccount: event.target.checked,
                    })
                  }
                />
                Work account required
              </label>
              <label>
                Engagement
                <select
                  aria-label='Engagement'
                  value={definition.audience.engagement}
                  onChange={(event) =>
                    update('audience', {
                      ...definition.audience,
                      engagement: event.target
                        .value as PromotionDefinition['audience']['engagement'],
                    })
                  }
                >
                  <option value='any'>Any</option>
                  <option value='medium'>
                    Medium: messaged on 2+ of the last 7 days
                  </option>
                  <option value='high'>
                    High: messaged on 4+ of the last 7 days
                  </option>
                </select>
              </label>
              <div className='promotion-feature-field'>
                <strong>Only users with these features switched on</strong>
                <div className='promotion-feature-flags'>
                  {featureFlags.map((flag) => (
                    <label className='promotion-check' key={flag}>
                      <input
                        type='checkbox'
                        checked={definition.audience.requiredFlags.includes(
                          flag,
                        )}
                        onChange={(event) =>
                          update('audience', {
                            ...definition.audience,
                            requiredFlags: event.target.checked
                              ? [...definition.audience.requiredFlags, flag]
                              : definition.audience.requiredFlags.filter(
                                  (requiredFlag) => requiredFlag !== flag,
                                ),
                          })
                        }
                      />
                      {flag}
                    </label>
                  ))}
                </div>
                <p className='promotion-field-lead'>
                  They need every ticked feature switched on.
                </p>
              </div>
              {isInvitePromotion(definition) && (
                <label className='promotion-schedule-delay promotion-invite-cooldown'>
                  Don&apos;t message users who were messaged about invites less
                  than{' '}
                  <select
                    aria-label='Invite cooldown days'
                    value={definition.audience.inviteCooldownDays ?? ''}
                    onChange={(event) =>
                      update('audience', {
                        ...definition.audience,
                        inviteCooldownDays: event.target.value
                          ? Number(event.target.value)
                          : null,
                      })
                    }
                  >
                    <option value=''>Off</option>
                    {[7, 14, 30, 60, 90].map((days) => (
                      <option key={days} value={days}>
                        {days}
                      </option>
                    ))}
                  </select>{' '}
                  days ago
                </label>
              )}
              <label>
                Holdout (%)
                <input
                  type='number'
                  min={0}
                  max={50}
                  value={definition.holdoutPercent}
                  onChange={(event) =>
                    update('holdoutPercent', Number(event.target.value))
                  }
                />
                <p className='promotion-field-lead'>
                  Share of the final audience, after filters, held back for
                  comparison.
                </p>
              </label>
              <div className='promotion-review-actions'>
                <button
                  type='button'
                  className='launch-btn launch-btn--quiet'
                  disabled={busy !== null}
                  onClick={onCheckAudience}
                >
                  {busy === 'preview' ? 'Checking…' : 'Check audience'}
                </button>
                {preview && (
                  <strong className='promotion-eligible-count'>
                    {preview.eligible} eligible
                  </strong>
                )}
              </div>
            </div>
            {preview && <div className='launch-card'>{previewContent}</div>}
          </PromotionEditorSection>

          <PromotionEditorSection
            number={3}
            title={PROMOTION_SECTION_TITLES[3]}
            summary={sectionSummary(3)}
            state={sectionState(3)}
            open={openSections.has(3)}
            onToggle={() => toggleSection(3)}
            onContinue={() => continueSection(3)}
            help={mobileSectionHelp(3)}
          >
            <div className='promotion-channel-options'>
              <div className='promotion-channel-group'>
                <label className='promotion-channel-option'>
                  <input
                    type='checkbox'
                    checked={definition.channels.includes('whatsapp')}
                    disabled={cardLocked}
                    onChange={(event) =>
                      setChannel('whatsapp', event.target.checked)
                    }
                  />
                  <span>
                    {definition.whatsapp.cardOnly
                      ? 'WhatsApp: card only'
                      : 'WhatsApp: message from the secretary'}
                  </span>
                  {cardLocked && (
                    <small className='promotion-channel-option__hint'>
                      Card already submitted
                    </small>
                  )}
                </label>
                {definition.channels.includes('whatsapp') && (
                  <div className='launch-card promotion-personal-message'>
                    <div className='launch-card-header'>
                      <h4>Personal message</h4>
                    </div>
                    <div
                      className='promotion-whatsapp-mode'
                      role='radiogroup'
                      aria-label='What to send on WhatsApp'
                    >
                      <label className='promotion-check'>
                        <input
                          type='radio'
                          name='promotion-whatsapp-mode'
                          checked={!definition.whatsapp.cardOnly}
                          onChange={() => updateWhatsApp({ cardOnly: false })}
                        />
                        Card or message
                      </label>
                      <label className='promotion-check'>
                        <input
                          type='radio'
                          name='promotion-whatsapp-mode'
                          checked={definition.whatsapp.cardOnly === true}
                          onChange={() => updateWhatsApp({ cardOnly: true })}
                        />
                        Card only
                      </label>
                    </div>
                    {definition.whatsapp.cardOnly ? (
                      <p className='launch-hint'>
                        Everyone gets the card, including people who messaged in
                        the last 24 hours. People who can&apos;t get a card are
                        skipped.
                      </p>
                    ) : (
                      <p className='launch-hint'>
                        Your secretary writes a one-off message from What the
                        secretary should know, written the way you describe
                        here. The link is added exactly as set, or as a "Get
                        your invite link" button.
                      </p>
                    )}
                    <label
                      className={
                        definition.whatsapp.cardOnly ? 'is-disabled' : undefined
                      }
                    >
                      How should the secretary write this message
                      <textarea
                        rows={4}
                        disabled={definition.whatsapp.cardOnly}
                        maxLength={1500}
                        value={definition.whatsapp.guidance}
                        onChange={(event) =>
                          updateWhatsApp({ guidance: event.target.value })
                        }
                      />
                    </label>
                    <label
                      className={
                        definition.whatsapp.cardOnly ? 'is-disabled' : undefined
                      }
                    >
                      Fallback message ({'{link}'} placeholder when using a
                      link, not with the invite button)
                      <textarea
                        rows={3}
                        disabled={definition.whatsapp.cardOnly}
                        maxLength={900}
                        value={definition.whatsapp.fallbackText}
                        onChange={(event) =>
                          updateWhatsApp({ fallbackText: event.target.value })
                        }
                      />
                    </label>
                    <PromotionCardEditor
                      card={definition.card}
                      onChange={(card) => update('card', card)}
                      slug={selectedSlug}
                      editable={current?.status === 'draft' || !current}
                      locked={cardLocked}
                      hasLink={definition.link !== null}
                      cardOnly={definition.whatsapp.cardOnly === true}
                      canSubmit={Boolean(
                        selectedSlug &&
                          current?.status === 'draft' &&
                          !hasUnsavedChanges &&
                          definition.card,
                      )}
                      approvals={detail?.cardTemplates ?? []}
                      auth={auth}
                      onSubmitted={onCardSubmitted}
                    />
                  </div>
                )}
              </div>
              <div className='promotion-channel-group'>
                <label className='promotion-channel-option'>
                  <input
                    type='checkbox'
                    checked={definition.channels.includes('briefing')}
                    onChange={(event) =>
                      setChannel('briefing', event.target.checked)
                    }
                  />
                  <span>Daily Briefing card</span>
                </label>
                {definition.channels.includes('briefing') &&
                  definition.briefing && (
                    <div className='launch-card promotion-briefing-card'>
                      <div className='launch-card-header'>
                        <h4>Daily Briefing card</h4>
                      </div>
                      <PromotionBriefingEditor
                        briefing={definition.briefing}
                        onChange={(briefing) => update('briefing', briefing)}
                        auth={auth}
                        editable
                      />
                    </div>
                  )}
              </div>
              <label className='promotion-channel-option is-disabled'>
                <input type='checkbox' disabled />
                <span>Email</span>
                <small>Coming later</small>
              </label>
              <p className='promotion-channel-cap'>
                Each person gets at most one promotion per channel and two
                channels in any 14 days.
              </p>
            </div>
          </PromotionEditorSection>

          <PromotionEditorSection
            number={4}
            title={PROMOTION_SECTION_TITLES[4]}
            summary={sectionSummary(4)}
            state={sectionState(4)}
            open={openSections.has(4)}
            onToggle={() => toggleSection(4)}
            onContinue={() => continueSection(4)}
            help={mobileSectionHelp(4)}
          >
            <div className='promotion-form'>
              <div className='promotion-inline-fields'>
                <div
                  className='promotion-tabs'
                  role='radiogroup'
                  aria-label='Schedule mode'
                >
                  <button
                    type='button'
                    role='radio'
                    aria-checked={definition.afterOnboardingDays === null}
                    className={`promotion-tab${definition.afterOnboardingDays === null ? ' is-active' : ''}`}
                    onClick={() =>
                      onChange({
                        ...definition,
                        afterOnboardingDays: null,
                        audience: {
                          ...definition.audience,
                          newUsersOnly: false,
                        },
                      })
                    }
                  >
                    Fixed dates
                  </button>
                  <button
                    type='button'
                    role='radio'
                    aria-checked={definition.afterOnboardingDays !== null}
                    className={`promotion-tab${definition.afterOnboardingDays !== null ? ' is-active' : ''}`}
                    onClick={() =>
                      update(
                        'afterOnboardingDays',
                        definition.afterOnboardingDays ?? 3,
                      )
                    }
                  >
                    Days after onboarding
                  </button>
                </div>
              </div>
              {definition.afterOnboardingDays !== null && (
                <label className='promotion-schedule-delay'>
                  Send
                  <input
                    aria-label='Days after onboarding'
                    type='number'
                    min={1}
                    max={90}
                    step={1}
                    value={definition.afterOnboardingDays}
                    onChange={(event) =>
                      update('afterOnboardingDays', Number(event.target.value))
                    }
                  />
                  days after they finish onboarding
                </label>
              )}
              <label>
                {definition.afterOnboardingDays === null
                  ? 'Start date'
                  : 'Start sending from'}
                <input
                  type='text'
                  inputMode='numeric'
                  placeholder='dd/mm/yyyy'
                  value={dateInputs.startDate}
                  onChange={(event) =>
                    onDateInputChange('startDate', event.target.value)
                  }
                />
              </label>
              <label>
                {definition.afterOnboardingDays === null
                  ? 'End date'
                  : 'Stop sending after'}
                <input
                  type='text'
                  inputMode='numeric'
                  placeholder='dd/mm/yyyy'
                  value={dateInputs.endDate}
                  onChange={(event) =>
                    onDateInputChange('endDate', event.target.value)
                  }
                />
              </label>
              <div className='promotion-weekdays'>
                <span>Part of day</span>
                {(
                  [
                    ['morning', 'Morning (9–12)'],
                    ['afternoon', 'Afternoon (13–17)'],
                    ['evening', 'Evening (17–20)'],
                  ] as const
                ).map(([part, label]) => (
                  <label className='promotion-check' key={part}>
                    <input
                      type='checkbox'
                      checked={definition.slot.partsOfDay.includes(part)}
                      onChange={(event) =>
                        update('slot', {
                          ...definition.slot,
                          partsOfDay: event.target.checked
                            ? [...definition.slot.partsOfDay, part]
                            : definition.slot.partsOfDay.filter(
                                (value) => value !== part,
                              ),
                        })
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
              <div className='promotion-weekdays'>
                <span>Weekdays</span>
                {WEEKDAYS.map((day, index) => (
                  <label className='promotion-check' key={day}>
                    <input
                      type='checkbox'
                      checked={definition.slot.weekdays.includes(index)}
                      onChange={(event) =>
                        update('slot', {
                          ...definition.slot,
                          weekdays: event.target.checked
                            ? [...definition.slot.weekdays, index].sort()
                            : definition.slot.weekdays.filter(
                                (value) => value !== index,
                              ),
                        })
                      }
                    />
                    {day}
                  </label>
                ))}
              </div>
            </div>
          </PromotionEditorSection>

          <PromotionEditorSection
            number={5}
            title={PROMOTION_SECTION_TITLES[5]}
            summary={sectionSummary(5)}
            state={sectionState(5)}
            open={openSections.has(5)}
            onToggle={() => toggleSection(5)}
            onContinue={() => continueSection(5)}
            help={mobileSectionHelp(5)}
          >
            <div className='promotion-review'>
              <div className='launch-card'>
                <div className='launch-card-header'>
                  <h4>Audience preview</h4>
                </div>
                {previewContent}
              </div>
              {(issues.length > 0 || allCardWarnings.length > 0) && (
                <div className='promotion-validation-note'>
                  <strong>
                    {issues.length > 0 ? 'Needs attention' : 'Warnings'}
                  </strong>
                  {issues.length > 0 && (
                    <ul>
                      {issues.map((entry, index) => (
                        <li key={`${entry.section}-${entry.message}-${index}`}>
                          {entry.message}
                        </li>
                      ))}
                    </ul>
                  )}
                  {allCardWarnings.length > 0 && (
                    <ul>
                      {allCardWarnings.map((warning, index) => (
                        <li key={`${warning}-${index}`}>{warning}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {definition.channels.includes('whatsapp') && (
                <div className='launch-card promotion-copy-summary'>
                  <div className='launch-card-header'>
                    <h4>WhatsApp message</h4>
                  </div>
                  {definition.whatsapp.cardOnly ? (
                    <p>
                      <strong>Sends:</strong> the card only, no message or
                      fallback.
                    </p>
                  ) : (
                    <>
                      <p>
                        <strong>How to write it:</strong>{' '}
                        {definition.whatsapp.guidance || 'Not set'}
                      </p>
                      <p>
                        <strong>Fallback:</strong>{' '}
                        {definition.whatsapp.fallbackText || 'Not set'}
                      </p>
                    </>
                  )}
                  {definition.card && (
                    <p>
                      <strong>Card:</strong>{' '}
                      {definition.card.body || 'Copy not set'}
                    </p>
                  )}
                </div>
              )}
              {definition.channels.includes('briefing') &&
                definition.briefing && (
                  <div className='launch-card promotion-copy-summary'>
                    <div className='launch-card-header'>
                      <h4>Daily Briefing card</h4>
                    </div>
                    <p>
                      <strong>
                        {definition.briefing.headline || 'Headline not set'}
                      </strong>
                    </p>
                    <p>{definition.briefing.body || 'Copy not set'}</p>
                    {definition.briefing.imageUrl && <p>Image attached</p>}
                  </div>
                )}
              {selectedSlug && (
                <PromotionTestPanel
                  slug={selectedSlug}
                  definition={definition}
                  cardApproved={Boolean(englishCardApproved)}
                  disabledReason={
                    hasUnsavedChanges ? 'Save your changes first' : null
                  }
                  auth={auth}
                />
              )}
            </div>
          </PromotionEditorSection>
        </div>
        <aside
          className='promotion-editor__rail'
          aria-label='How this section works'
        >
          <PromotionSectionHelp
            number={activeSection}
            title={PROMOTION_SECTION_TITLES[activeSection]}
          />
        </aside>
      </div>
    </div>
  );
};

export default PromotionEditor;
